import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../config/api';
import MainHeader from '../../components/MainHeader';
import Footer from '../../components/Footer';
import './CustomListsScreen.css';

const EMOJI_OPTIONS = ['🍽️', '🍕', '🍔', '🌮', '🍣', '🥗', '🍜', '🥩', '🍝', '☕', '🍰', '🔥', '❤️', '⭐', '🌙', '🎉'];

export default function CustomListsScreen() {
  const navigate = useNavigate();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmoji, setNewEmoji] = useState('🍽️');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const fetchLists = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/custom-lists');
      setLists(data.lists || []);
    } catch (err) {
      console.error('Failed to load lists:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLists();
  }, [fetchLists]);

  async function handleCreate(e) {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    setError('');
    try {
      await api.post('/custom-lists', { name: newName.trim(), emoji: newEmoji });
      setNewName('');
      setNewEmoji('🍽️');
      setShowCreate(false);
      fetchLists();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create list');
    } finally {
      setCreating(false);
    }
  }

  async function deleteList(listId, name) {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    setLists(prev => prev.filter(l => l.id !== listId));
    try {
      await api.delete(`/custom-lists/${listId}`);
    } catch {
      fetchLists();
    }
  }

  return (
    <div className="main-screen">
      <MainHeader 
        title="My Lists" 
        showBack={true} 
        backPath="/home"
        rightButton={
          <button className="header-add-btn" onClick={() => setShowCreate(true)}>
            + New
          </button>
        }
      />

      <main className="screen-main">
        <div className="lists-container">
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading lists...</p>
            </div>
          ) : lists.length === 0 && !showCreate ? (
            <div className="empty-state">
              <div className="empty-emoji">📋</div>
              <h2 className="empty-title">No lists yet</h2>
              <p className="empty-text">
                Create custom restaurant lists for date nights, group favorites, or any occasion.
              </p>
              <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
                + Create Your First List
              </button>
            </div>
          ) : (
            <>
              {showCreate && (
                <form className="create-list-form" onSubmit={handleCreate}>
                  <h3>Create New List</h3>
                  <div className="emoji-picker">
                    {EMOJI_OPTIONS.map(e => (
                      <button
                        key={e}
                        type="button"
                        className={`emoji-option ${newEmoji === e ? 'active' : ''}`}
                        onClick={() => setNewEmoji(e)}
                      >
                        {e}
                      </button>
                    ))}
                  </div>
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="List name (e.g., Date Night Spots)"
                    className="list-name-input"
                    maxLength={50}
                    autoFocus
                  />
                  {error && <p className="form-error">{error}</p>}
                  <div className="form-actions">
                    <button type="button" className="btn btn-secondary" onClick={() => { setShowCreate(false); setError(''); }}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={creating || !newName.trim()}>
                      {creating ? 'Creating...' : 'Create List'}
                    </button>
                  </div>
                </form>
              )}

              <div className="lists-grid">
                {lists.map(list => (
                  <div
                    key={list.id}
                    className="list-card"
                    onClick={() => navigate(`/lists/${list.id}`, { state: { listName: list.name, listEmoji: list.emoji } })}
                  >
                    <div className="list-card-header">
                      <span className="list-emoji">{list.emoji}</span>
                      <button
                        className="list-delete-btn"
                        onClick={(e) => { e.stopPropagation(); deleteList(list.id, list.name); }}
                        title="Delete list"
                      >
                        🗑️
                      </button>
                    </div>
                    <h3 className="list-name">{list.name}</h3>
                    <p className="list-count">
                      {list.restaurantCount || 0} restaurant{(list.restaurantCount || 0) !== 1 ? 's' : ''}
                    </p>
                  </div>
                ))}

                <div className="list-card add-card" onClick={() => setShowCreate(true)}>
                  <span className="add-icon">+</span>
                  <p className="add-label">New List</p>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
